import React, { useState, useEffect } from 'react';
import {
    RadioGroup, FormControlLabel, Radio, Typography, Box,
    Alert, Snackbar, CircularProgress,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import userService from '../services/UserService';
import authService from '../services/AuthService';
import HelpButton from './HelpButton';
import { useTranslation } from 'react-i18next';

const Settings = () => {
    const { t } = useTranslation();

    const [mfaMethod,  setMfaMethod]  = useState('');
    const [loading,    setLoading]    = useState(true);
    const [saveError,  setSaveError]  = useState('');
    const [saveOk,     setSaveOk]     = useState(false);

    useEffect(() => {
        userService.getUser(authService.getCurrentUser())
            .then((user) => setMfaMethod(user?.mfa ?? ''))
            .catch((err) => {
                console.warn('Could not load MFA setting:', err.message);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleMfaChange = (event) => {
        const next = event.target.value;
        setMfaMethod(next);
        userService.updateUser({ username: authService.getCurrentUser(), mfa: next })
            .then(() => setSaveOk(true))
            .catch((err) => {
                console.error('Failed to update MFA:', err.message);
                setSaveError(t('settings.saveError', 'Failed to save setting.'));
            });
    };

    return (
        <Box sx={{ px: 1 }}>
            {/* ── Page header ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                            {t('sideBar.settings', 'Settings')}
                        </Typography>
                        <HelpButton />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {t('settings.subtitle', 'Account security preferences')}
                    </Typography>
                </Box>
            </Box>

            {/* ── MFA card ── */}
            <Box
                sx={{
                    maxWidth: 440,
                    bgcolor: 'background.paper',
                    border: 1.5,
                    borderColor: 'divider',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                    overflow: 'hidden',
                }}
            >
                {/* Card header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2.5,
                        py: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)',
                    }}
                >
                    <ShieldIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                        {t('settings.settings', 'Multi-Factor Authentication')}
                    </Typography>
                </Box>

                {/* Card body */}
                <Box sx={{ px: 2.5, py: 2.5 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} color="primary" />
                        </Box>
                    ) : (
                        <RadioGroup value={mfaMethod} onChange={handleMfaChange}>
                            {[
                                { value: 'SMS_MFA',   label: t('settings.sms',   'SMS') },
                                { value: 'EMAIL_OTP', label: t('settings.email', 'Email OTP') },
                                { value: 'NOMFA',     label: t('settings.none',  'None') },
                            ].map(({ value, label }) => (
                                <FormControlLabel
                                    key={value}
                                    value={value}
                                    label={label}
                                    control={
                                        <Radio />
                                    }
                                    sx={{
                                        mx: 0,
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        mb: 0.5,
                                        transition: 'background-color 0.15s',
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.9rem',
                                            fontWeight: mfaMethod === value ? 600 : 400,
                                            color: mfaMethod === value ? 'primary.main' : 'text.primary',
                                        },
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    )}
                </Box>
            </Box>

            {/* ── Snackbars ── */}
            <Snackbar open={saveOk} autoHideDuration={2500} onClose={() => setSaveOk(false)}>
                <Alert severity="success" onClose={() => setSaveOk(false)} sx={{ width: '100%' }}>
                    {t('settings.saved', 'Setting saved.')}
                </Alert>
            </Snackbar>
            <Snackbar open={Boolean(saveError)} autoHideDuration={3000} onClose={() => setSaveError('')}>
                <Alert severity="error" onClose={() => setSaveError('')} sx={{ width: '100%' }}>
                    {saveError}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Settings;
